import { API_BASE_URL } from "./apiConfig";
import { logApiContext } from "../config/apiBaseUrl";

const API_URL = `${API_BASE_URL}/api/services`;
const logRequest = (action, url) =>
  logApiContext(`serviceService:${action}`, { url, API_URL });

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const toCoords = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const [lng, lat] = raw.map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  if (raw.coordinates && Array.isArray(raw.coordinates)) {
    const [lng, lat] = raw.coordinates.map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(raw.lat ?? raw.latitude);
  const lng = Number(raw.lng ?? raw.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const mapServiceToStart = (svc) => {
  if (!svc) return null;
  const raw = svc.posicionInicio || svc.posicioninicio || svc.startPosition || svc.posicion;
  const coords = toCoords(raw);
  const id = svc._id || svc.id || svc.externalId;
  if (!coords || !id) return null;
  return { id, posicionInicio: { coordinates: [coords.lng, coords.lat] } };
};

export const fetchServices = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.startDate) query.append("startDate", params.startDate);
  if (params.endDate) query.append("endDate", params.endDate);
  if (params.paymentMethod) query.append("paymentMethod", params.paymentMethod);
  if (params.closureId) query.append("closureId", params.closureId);

  const queryString = query.toString();
  const url = queryString ? `${API_URL}?${queryString}` : API_URL;
  logRequest("fetchServices", url);
  const res = await fetch(url, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Error cargando servicios");
  return res.json();
};

export const fetchServiceById = async (id) => {
  const url = `${API_URL}/${id}`;
  logRequest("fetchServiceById", url);
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error((await res.json()).message || "Error cargando servicio");
  return res.json();
};

export const createService = async (payload) => {
  logRequest("createService", API_URL);
  const res = await fetch(API_URL, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Error creando servicio");
  return res.json();
};

export const updateService = async (id, payload) => {
  const url = `${API_URL}/${id}`;
  logRequest("updateService", url);
  const res = await fetch(url, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Error actualizando servicio");
  return res.json();
};

export const updateServiceComment = async (id, comentario) => {
  const url = `${API_URL}/${id}/comment`;
  logRequest("updateServiceComment", url);
  const res = await fetch(url, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ comentario }),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Error guardando comentario");
  return res.json();
};

export const deleteService = async (id) => {
  const url = `${API_URL}/${id}`;
  logRequest("deleteService", url);
  const res = await fetch(url, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Error eliminando servicio");
  return res.json();
};

export const toggleCancelService = async (id, cancelado = true) => {
  const url = `${API_URL}/${id}/cancel`;
  logRequest("toggleCancelService", url);
  const res = await fetch(url, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ cancelado }),
  });
  if (!res.ok) throw new Error((await res.json()).message || "No se pudo actualizar el estado del servicio");
  return res.json();
};

export const fetchServiceStartCoordinates = async ({ startDate, endDate, drivers = [] } = {}) => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (Array.isArray(drivers) && drivers.length) {
    drivers.forEach((d) => params.append("drivers", d));
  }
  const url = `${API_URL}/start-coordinates?${params.toString()}`;
  logRequest("fetchServiceStartCoordinates", url);
  const res = await fetch(url, { headers: authHeaders() });
  if (res.ok) {
    const payload = await res.json();
    return Array.isArray(payload) ? payload.map(mapServiceToStart).filter(Boolean) : [];
  }

  const services = await fetchServices({
    startDate,
    endDate,
    driverIds: Array.isArray(drivers) && drivers.length ? drivers : undefined,
  });
  return services.map(mapServiceToStart).filter(Boolean);
};
